import React from "react";
import { Icon } from "../../UIElements/Icon";

const Pagination = ({
  currentPage = 1,
  totalPages = 1,
  onPageChange,
  showPageNumbers = true,
  maxVisiblePages = 5,
  showFirstLast = true,
  showPrevNext = true,
  className = "",
  disabled = false,
  ariaLabel,
}) => {
  // Don't render if there's only one page or no pages
  if (totalPages <= 1) return null;

  // Calculate visible page numbers
  const getVisiblePages = () => {
    const pages = [];
    const halfVisible = Math.floor(maxVisiblePages / 2);

    let startPage = Math.max(1, currentPage - halfVisible);
    let endPage = Math.min(totalPages, currentPage + halfVisible);

    // Adjust if we're near the beginning or end
    if (endPage - startPage + 1 < maxVisiblePages) {
      if (startPage === 1) {
        endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
      } else {
        startPage = Math.max(1, endPage - maxVisiblePages + 1);
      }
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  };

  const visiblePages = getVisiblePages();
  const isFirstPage = currentPage === 1;
  const isLastPage = currentPage === totalPages;

  const handlePageChange = (page) => {
    if (disabled || page < 1 || page > totalPages || page === currentPage)
      return;
    onPageChange(page);
  };

  return (
    <nav
      data-testid="pagination"
      aria-label={ariaLabel}
      className={`text-muted-foreground flex items-center justify-center gap-1 tabular-nums ${className}`}
    >
      {/* First Page Button - Hidden on mobile */}
      {showFirstLast && (
        <button
          type="button"
          onClick={() => handlePageChange(1)}
          disabled={disabled || isFirstPage}
          className={`focus-visible:ring-ring active:bg-accent hidden min-h-11 min-w-11 items-center justify-center rounded-md px-2 py-2 text-xs font-medium transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none motion-reduce:transition-none sm:flex sm:px-3 sm:text-sm forced-colors:border forced-colors:border-[ButtonBorder] ${
            disabled || isFirstPage
              ? "text-muted-foreground cursor-not-allowed opacity-50"
              : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
          }`}
          title="First page"
        >
          <Icon
            name="ChevronsLeft"
            className="h-3 w-3 sm:h-4 sm:w-4 rtl:rotate-180"
          />
        </button>
      )}

      {/* Previous Page Button */}
      {showPrevNext && (
        <button
          type="button"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={disabled || isFirstPage}
          className={`focus-visible:ring-ring active:bg-accent flex min-h-11 min-w-11 items-center justify-center rounded-md px-2 py-2 text-xs font-medium transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none motion-reduce:transition-none sm:px-3 sm:text-sm forced-colors:border forced-colors:border-[ButtonBorder] ${
            disabled || isFirstPage
              ? "text-muted-foreground cursor-not-allowed opacity-50"
              : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
          }`}
          title="Previous page"
        >
          <Icon
            name="ChevronLeft"
            className="h-3 w-3 sm:h-4 sm:w-4 rtl:rotate-180"
          />
        </button>
      )}

      {/* Page Numbers */}
      {showPageNumbers && (
        <>
          {/* Show ellipsis if there are pages before the visible range - Hidden on mobile */}
          {visiblePages[0] > 1 && (
            <>
              <button
                type="button"
                onClick={() => handlePageChange(1)}
                disabled={disabled}
                className={`focus-visible:ring-ring active:bg-accent hidden min-h-11 min-w-11 items-center justify-center rounded-md px-2 py-2 text-xs font-medium transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none motion-reduce:transition-none sm:flex sm:px-3 sm:text-sm forced-colors:border forced-colors:border-[ButtonBorder] ${
                  disabled
                    ? "text-muted-foreground cursor-not-allowed opacity-50"
                    : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                }`}
              >
                1
              </button>
              {visiblePages[0] > 2 && (
                <span className="text-muted-foreground hidden px-1 py-2 text-xs sm:block sm:px-2 sm:text-sm">
                  ...
                </span>
              )}
            </>
          )}

          {/* Visible page numbers */}
          {visiblePages.map((page) => (
            <button
              key={page}
              type="button"
              onClick={() => handlePageChange(page)}
              disabled={disabled}
              aria-current={page === currentPage ? "page" : undefined}
              className={`focus-visible:ring-ring active:bg-accent flex min-h-11 min-w-11 items-center justify-center rounded-md px-2 py-2 text-xs font-medium transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none motion-reduce:transition-none sm:px-3 sm:text-sm forced-colors:border forced-colors:border-[ButtonBorder] ${
                page === currentPage
                  ? "bg-primary text-primary-foreground"
                  : disabled
                    ? "text-muted-foreground cursor-not-allowed opacity-50"
                    : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
              }`}
            >
              {page}
            </button>
          ))}

          {/* Show ellipsis if there are pages after the visible range - Hidden on mobile */}
          {visiblePages[visiblePages.length - 1] < totalPages && (
            <>
              {visiblePages[visiblePages.length - 1] < totalPages - 1 && (
                <span className="text-muted-foreground hidden px-1 py-2 text-xs sm:block sm:px-2 sm:text-sm">
                  ...
                </span>
              )}
              <button
                type="button"
                onClick={() => handlePageChange(totalPages)}
                disabled={disabled}
                className={`focus-visible:ring-ring active:bg-accent hidden min-h-11 min-w-11 items-center justify-center rounded-md px-2 py-2 text-xs font-medium transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none motion-reduce:transition-none sm:flex sm:px-3 sm:text-sm forced-colors:border forced-colors:border-[ButtonBorder] ${
                  disabled
                    ? "text-muted-foreground cursor-not-allowed opacity-50"
                    : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
                }`}
              >
                {totalPages}
              </button>
            </>
          )}
        </>
      )}

      {/* Next Page Button */}
      {showPrevNext && (
        <button
          type="button"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={disabled || isLastPage}
          className={`focus-visible:ring-ring active:bg-accent flex min-h-11 min-w-11 items-center justify-center rounded-md px-2 py-2 text-xs font-medium transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none motion-reduce:transition-none sm:px-3 sm:text-sm forced-colors:border forced-colors:border-[ButtonBorder] ${
            disabled || isLastPage
              ? "text-muted-foreground cursor-not-allowed opacity-50"
              : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
          }`}
          title="Next page"
        >
          <Icon
            name="ChevronRight"
            className="h-3 w-3 sm:h-4 sm:w-4 rtl:rotate-180"
          />
        </button>
      )}

      {/* Last Page Button - Hidden on mobile */}
      {showFirstLast && (
        <button
          type="button"
          onClick={() => handlePageChange(totalPages)}
          disabled={disabled || isLastPage}
          className={`focus-visible:ring-ring active:bg-accent hidden min-h-11 min-w-11 items-center justify-center rounded-md px-2 py-2 text-xs font-medium transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none motion-reduce:transition-none sm:flex sm:px-3 sm:text-sm forced-colors:border forced-colors:border-[ButtonBorder] ${
            disabled || isLastPage
              ? "text-muted-foreground cursor-not-allowed opacity-50"
              : "text-muted-foreground hover:bg-accent hover:text-accent-foreground"
          }`}
          title="Last page"
        >
          <Icon
            name="ChevronsRight"
            className="h-3 w-3 sm:h-4 sm:w-4 rtl:rotate-180"
          />
        </button>
      )}
    </nav>
  );
};

export default Pagination;
